import moment from "moment";
import { projectBidStatusObj } from "./config";

import {
  ProjectResponseModel,
  ProjectsSortOptionsModel,
} from "../models/ProjectResponseModel";
import { LoggedInUserModel } from "../models/UserModel";

export const yesOrNoAutoCompleteOptions = [
  { value: 0, label: "No" },
  { value: 1, label: "Yes" },
];

export const statusOptions = [
  {
    value: "active",
    label: "Active",
  },
  {
    value: "inactive",
    label: "Inactive",
  },
];

export function formatDate(date?: Date, noYear: boolean = false): string {
  // month is zero-based, days and years are one-based in datetime JS
  if (date instanceof Date) {
    return `${date.getMonth() + 1}/${date.getDate()}${noYear ? "" : `/${date.getFullYear()}`
      }`;
  }
  return "";
}

export function isValidDate(date: any) {
  return !isNaN(date) && date instanceof Date;
}

// Truncate a string with specific length
export const trimEllip = (str: string, limit: number = 30) => {
  if (str.length > limit) {
    return str.substring(0, limit) + "...";
  }
  return str;
};

export const getStorageValue = (key: string) => {
  return sessionStorage.getItem(key)
    ? JSON.parse(sessionStorage.getItem(key)!)
    : null;
};

export const setStorageValue = (key: string, value: any) => {
  sessionStorage.setItem(key, JSON.stringify(value));
};

export const removeStorageValue = (key: string) => {
  sessionStorage.removeItem(key);
};

// To generate random numbers
export const generateRandomNumber = (length: number = 4): number => {
  const padLen = length - 1;
  const min = parseInt("1".padEnd(padLen, "0"));
  const max = parseInt("9".padEnd(padLen, "9"));
  return Math.floor(Math.random() * (max - min + 1)) + min;
};

// Function to get the abbrivation from string
export const getAbbreviation = (name: string) => {
  if (name.includes(" ")) {
    return name
      .split(" ")
      .map((token) => token[0])
      .slice(0, 2)
      .join("")
      .toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
};

// To get the status of the project
export const getProjectStatus = (
  project: ProjectResponseModel,
  loggedInUser?: LoggedInUserModel
): ProjectsSortOptionsModel => {
  const projectStatus = project?.projectStatus.toLowerCase();
  if (projectStatus === projectBidStatusObj.loss) {
    return {
      key: projectBidStatusObj.loss,
      value: "Lost",
    };
  }
  if (projectStatus === projectBidStatusObj.win) {
    return {
      key: projectBidStatusObj.win,
      value: "Won",
    };
  }
  if (projectStatus === projectBidStatusObj.bidSubmitted) {
    return {
      key: "bidSubmitted",
      value: "Bid Submitted",
    };
  }
  if (projectStatus === projectBidStatusObj.rejected) {
    return {
      key: projectBidStatusObj.rejected,
      value: "Rejected",
    };
  }
  if (projectStatus === projectBidStatusObj.approved) {
    return {
      key: projectBidStatusObj.approved,
      value: "Approved",
    };
  }
  if (projectStatus === projectBidStatusObj.submitted) {
    if (loggedInUser && loggedInUser.roleId === 3
      && project.buUserID === loggedInUser.userId
      && (project.isBUApprovalRequired === true ? project.isBUApproved !== true : false)) {
      return {
        key: projectBidStatusObj.pendingForApproval,
        value: "Pending For Approval",
      }
    }
    if (loggedInUser && loggedInUser.roleId === 3
      && project.sbuUserID === loggedInUser.userId
      && project.isSBUApproved !== true
      && (project.isBUApprovalRequired === true ? project.isBUApproved === true : true)) {
      return {
        key: projectBidStatusObj.pendingForApproval,
        value: "Pending For Approval",
      }
    }
    else if (loggedInUser && loggedInUser.roleId === 4
      && project.sbgUserID === loggedInUser.userId
      && project.isSBGApproved !== true
      && project.isSBUApproved === true
      && (project.isBUApprovalRequired === true ? project.isBUApproved === true : true)) {
      return {
        key: projectBidStatusObj.pendingForApproval,
        value: "Pending For Approval",
      }
    }
    else if (loggedInUser && loggedInUser.roleId === 5
      && project.csgoUserID === loggedInUser.userId
      && ((project.isBaseLoad !== true || project.isGreenListCustomer !== true || project.enforceCsgoApproval === true) ? project.isCSGOApproved !== true : false)
      && project.isSBGApproved === true
      && project.isSBUApproved === true
      && (project.isBUApprovalRequired === true ? project.isBUApproved === true : true)) {
      return {
        key: projectBidStatusObj.pendingForApproval,
        value: "Pending For Approval",
      }
    }
    else {
      return {
        key: projectBidStatusObj.submitted,
        value: "Submitted",
      };
    }
  }
  if (projectStatus === projectBidStatusObj.drafted) {
    return {
      key: projectBidStatusObj.drafted,
      value: "Completed",
    };
  }
  if (projectStatus === projectBidStatusObj.droppedAfterGoNoGo) {
    return {
      key: projectBidStatusObj.droppedAfterGoNoGo,
      value: "Dropped After GoNoGo",
    };
  }
  if (projectStatus === projectBidStatusObj.dropped) {
    return {
      key: projectBidStatusObj.dropped,
      value: "Dropped",
    };
  }
  return {
    key: projectBidStatusObj.savedAsDraft,
    value: "Saved As Draft",
  };
};

export const displayDateFormart = (
  val: Date | string | undefined,
  format: string = "YYYY-MM-DD"
): string => {
  if (!val) {
    return "";
  }
  if (typeof val === "string") {
    val = new Date(val);
  }
  if (val instanceof Date && !isNaN(val.getTime())) {
    return moment(val).format(format);
  }
  return "";
};

// To show the amount as thousand superator
export const showThousandSuperator = (value: string | number) => {
  if (typeof value === "string") {
    const numberValue = parseInt(value, 10);
    if (isNaN(numberValue)) {
      return value;
    }
    value = numberValue;
  }
  return value.toLocaleString("en-IN");
};

// format the result suit for auto-complete
export const createAutocompleteOptions = (
  data: any[],
  valueKey: string,
  labelKey1: string,
  labelKey2?: string
) =>
  data.reduce(
    (acc, val) => [
      ...acc,
      {
        value: val[valueKey],
        label: labelKey2
          ? `${val[labelKey1]} ${val[labelKey2]}`
          : val[labelKey1],
      },
    ],
    []
  );

// Debouncing function
export function debounceFun(fn: Function, delay: number = 500) {
  let timer: NodeJS.Timeout;
  return function (this: any, ...args: any) {
    clearTimeout(timer);
    const context = this;
    timer = setTimeout(() => {
      fn.apply(context, args);
    }, delay);
  };
}

export const isValidArr = (arr: any) =>
  Array.isArray(arr) && arr.length ? true : false;

export const isValidObj = (obj: any) =>
  obj !== null &&
    typeof obj === "object" &&
    Array.isArray(obj) === false &&
    Object.keys(obj).length
    ? true
    : false;

export const stringifyDate = (date: Date) => {
  if (!date) return;
  return moment(date).format();
};
export const getDecimalValue = (val: string, returnDecimal?:boolean) => {
  let value = parseFloat(val).toString();
  if (!val) {
    return "0";
  } else {
    if (value.split(".")[1]?.length > 1) {
      if(returnDecimal){
        let twoDecimalValue = value.split(".")[0] +"."+ value.split(".")[1].slice(0,1)
        return twoDecimalValue
      }else{
        return;
      }
    } else {
      if (value.includes(".")) {
        value =
          parseFloat(value.split(".")[0]).toString() +
          "." +
          value.split(".")[1];
      } else {
        value = parseFloat(value).toString();
      }
      return value;
    }
  }
};
